import { calculateLibraryIndicator, formatForChart } from '../services/indicators/libraryIndicatorService';

export const CHECKLIST_INDICATOR_OPTIONS = [
  { value: 'RSI', label: 'RSI' },
  { value: 'SMA', label: 'SMA' },
  { value: 'EMA', label: 'EMA' },
  { value: 'CCI', label: 'CCI' },
  { value: 'ADX', label: 'ADX' },
];

export const CHECKLIST_OVERLAY_IDS = new Set(['SMA', 'EMA', 'WMA', 'DEMA', 'TEMA']);

export const CHECKLIST_CONDITION_OPS = ['above', 'below', 'crossing_up', 'crossing_down'];

/** Shared condition options for oscillators and overlays. */
export const CHECKLIST_OP_OPTIONS = [
  { value: 'above', label: 'Above' },
  { value: 'below', label: 'Below' },
  { value: 'crossing_up', label: 'Crossing up' },
  { value: 'crossing_down', label: 'Crossing down' },
];

/** @deprecated Use CHECKLIST_OP_OPTIONS — kept as alias for existing imports. */
export const CHECKLIST_PRICE_OP_OPTIONS = CHECKLIST_OP_OPTIONS;

export const CHECKLIST_ITEM_TYPE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'indicator', label: 'Indicator' },
];

export const DEFAULT_CHECKLIST_SETTINGS = {
  enabled: true,
  block_until_complete: false,
  panel_collapsed_default: true,
};

export const DEFAULT_CHECKLIST_TEMPLATE = { items: [] };

export function isOverlayIndicator(id) {
  return CHECKLIST_OVERLAY_IDS.has(String(id || '').toUpperCase());
}

/** Map legacy numeric ops + aliases onto above/below/crossing_*. */
export function normalizeConditionOp(op, { overlay = false } = {}) {
  const raw = String(op || '').toLowerCase();
  if (CHECKLIST_CONDITION_OPS.includes(raw)) return raw;
  if (raw === 'gt' || raw === 'gte' || raw === '>') return 'above';
  if (raw === 'lt' || raw === 'lte' || raw === '<') return 'below';
  if (raw === 'cross_up' || raw === 'crossup') return 'crossing_up';
  if (raw === 'cross_down' || raw === 'crossdown') return 'crossing_down';
  return overlay ? 'above' : 'below';
}

/** Controlled number inputs: keep '' while clearing so digits aren't prefixed with 0. */
export function parseNumberInput(raw) {
  if (raw === '' || raw == null) return '';
  const n = Number(raw);
  return Number.isFinite(n) ? n : '';
}

export function coerceFiniteNumber(value) {
  if (value === '' || value == null) return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

export function normalizeChecklistTemplate(template) {
  const src = template && typeof template === 'object' ? template : {};
  const items = Array.isArray(src.items) ? src.items : [];
  return {
    ...src,
    items: items.map((item, i) => normalizeChecklistItem(item, i)),
  };
}

export function normalizeChecklistItem(item, index = 0) {
  const type = item?.type === 'indicator' ? 'indicator' : 'manual';
  const normalized = {
    ...item,
    key: item?.key || `item_${index + 1}`,
    label: typeof item?.label === 'string' ? item.label : '',
    type,
    position: typeof item?.position === 'number' ? item.position : index,
  };
  if (type === 'indicator') {
    const ind = item?.indicator && typeof item.indicator === 'object' ? item.indicator : {};
    const id = ind.id || 'RSI';
    const overlay = isOverlayIndicator(id);
    const rawPeriod = coerceFiniteNumber(ind.params?.time_period ?? ind.params?.period);
    const valueNum = coerceFiniteNumber(ind.value);
    let timePeriod = Number.isFinite(rawPeriod) && rawPeriod >= 2 ? rawPeriod : null;
    if (overlay && Number.isFinite(valueNum) && valueNum >= 2 && valueNum <= 500) {
      if (timePeriod == null || (timePeriod === 14 && valueNum !== 14 && valueNum !== 30)) {
        timePeriod = valueNum;
      }
    }
    if (timePeriod == null) timePeriod = 14;

    normalized.indicator = {
      id,
      params: {
        ...(ind.params && typeof ind.params === 'object' ? ind.params : {}),
        time_period: timePeriod,
        series_type: ind.params?.series_type || 'close',
      },
      op: normalizeConditionOp(ind.op, { overlay }),
      value: overlay
        ? timePeriod
        : (Number.isFinite(valueNum) ? valueNum : 30),
    };
  }
  return normalized;
}

export function normalizeChecklistSettings(settings) {
  const src = settings && typeof settings === 'object' ? settings : {};
  return {
    // Missing/legacy rows stay on — only explicit false disables the panel tab.
    enabled: src.enabled == null ? true : !!src.enabled,
    block_until_complete: !!src.block_until_complete,
    panel_collapsed_default: !!src.panel_collapsed_default,
  };
}

/**
 * Evaluate above/below/crossing against a reference level (threshold or MA).
 * For overlays, `actual`/`prevActual` are price; `expected`/`prevExpected` are indicator.
 * For oscillators, `actual`/`prevActual` are indicator; `expected` is threshold (prevExpected unused).
 */
export function evaluateConditionOp(op, {
  actual,
  expected,
  prevActual = null,
  prevExpected = null,
} = {}) {
  const normalizedOp = normalizeConditionOp(op);
  if (!Number.isFinite(actual) || !Number.isFinite(expected)) return false;

  switch (normalizedOp) {
    case 'above':
      return actual > expected;
    case 'below':
      return actual < expected;
    case 'crossing_up': {
      if (!Number.isFinite(prevActual) || !Number.isFinite(prevExpected)) return false;
      return prevActual <= prevExpected && actual > expected;
    }
    case 'crossing_down': {
      if (!Number.isFinite(prevActual) || !Number.isFinite(prevExpected)) return false;
      return prevActual >= prevExpected && actual < expected;
    }
    default:
      return false;
  }
}

/** @deprecated Prefer evaluateConditionOp — kept for older tests/callers. */
export function compareOp(op, actual, expected) {
  const mapped = normalizeConditionOp(op);
  if (mapped === 'crossing_up' || mapped === 'crossing_down') {
    return false;
  }
  return evaluateConditionOp(mapped, { actual, expected });
}

export function opLabel(op) {
  switch (normalizeConditionOp(op)) {
    case 'above': return 'above';
    case 'below': return 'below';
    case 'crossing_up': return 'crossing up';
    case 'crossing_down': return 'crossing down';
    default: return String(op || '');
  }
}

/** @deprecated Use opLabel */
export function opSymbol(op) {
  return opLabel(op);
}

/**
 * Human-readable condition, e.g. "RSI(14) below 30" or "Price crossing up EMA(20)".
 */
export function formatIndicatorCondition(indicator) {
  const ind = indicator && typeof indicator === 'object' ? indicator : {};
  const id = String(ind.id || 'RSI').toUpperCase();
  const period = ind.params?.time_period ?? ind.params?.period ?? 14;
  const label = opLabel(ind.op);
  if (isOverlayIndicator(id)) {
    return `Price ${label} ${id}(${period})`;
  }
  const threshold = Number.isFinite(Number(ind.value)) ? Number(ind.value) : '—';
  return `${id}(${period}) ${label} ${threshold}`;
}

/**
 * Subtext under a checklist indicator row.
 * e.g. "Price above EMA(20) · 744.20 / 744.48" or "RSI(14) crossing up 30 · 28.1 → 31.4"
 */
export function formatIndicatorDetail(row) {
  if (!row || row.type !== 'indicator') return null;
  const condition = row.condition || formatIndicatorCondition({
    id: row.indicatorId,
    op: row.op,
    value: row.compareLeft === 'price' ? row.period : row.expected,
    params: { time_period: row.period },
  });
  const op = normalizeConditionOp(row.op);
  const isCross = op === 'crossing_up' || op === 'crossing_down';

  if (row.compareLeft === 'price') {
    if (Number.isFinite(Number(row.close)) && Number.isFinite(Number(row.indicatorValue))) {
      if (isCross
        && Number.isFinite(Number(row.prevClose))
        && Number.isFinite(Number(row.prevIndicatorValue))) {
        return `${condition} · ${Number(row.prevClose).toFixed(2)}/${Number(row.prevIndicatorValue).toFixed(2)} → ${Number(row.close).toFixed(2)}/${Number(row.indicatorValue).toFixed(2)}`;
      }
      return `${condition} · ${Number(row.close).toFixed(2)} / ${Number(row.indicatorValue).toFixed(2)}`;
    }
    return condition;
  }

  if (isCross && Number.isFinite(Number(row.prevActual)) && Number.isFinite(Number(row.actual))) {
    return `${condition} · ${Number(row.prevActual).toFixed(2)} → ${Number(row.actual).toFixed(2)}`;
  }
  if (Number.isFinite(Number(row.actual))) {
    return `${condition} · now ${Number(row.actual).toFixed(2)}`;
  }
  return condition;
}

/**
 * Convert TradeStation / stream bars (Close, TimeStamp) into library bars (close, time).
 * Also accepts already-normalized chart bars.
 */
export function normalizeBarsForIndicator(bars) {
  if (!Array.isArray(bars) || bars.length === 0) return [];

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const resolveTimeSec = (bar) => {
    if (bar?.time != null) {
      const t = Number(bar.time);
      if (Number.isFinite(t) && t > 1e12) return Math.floor(t / 1000); // ms
      if (Number.isFinite(t) && t > 0) return Math.floor(t); // seconds
    }
    if (bar?.Epoch != null) {
      const epochMs = Number(bar.Epoch);
      if (Number.isFinite(epochMs) && epochMs > 0) {
        return Math.floor(epochMs > 1e12 ? epochMs / 1000 : epochMs);
      }
    }
    const ts = bar?.TimeStamp || bar?.TradeTime;
    if (!ts) return null;
    const ms = new Date(ts).getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  };

  const byTime = new Map();
  for (const bar of bars) {
    const time = resolveTimeSec(bar);
    const open = safeNum(bar.open ?? bar.Open);
    const high = safeNum(bar.high ?? bar.High);
    const low = safeNum(bar.low ?? bar.Low);
    const close = safeNum(bar.close ?? bar.Close);
    const volume = safeNum(bar.volume ?? bar.TotalVolume ?? bar.Volume) || 0;
    if (time == null || open == null || high == null || low == null || close == null) continue;
    byTime.set(time, { time, open, high, low, close, volume });
  }
  return Array.from(byTime.values()).sort((a, b) => a.time - b.time);
}

export function latestClose(bars) {
  const normalized = normalizeBarsForIndicator(bars);
  if (!normalized.length) return null;
  return normalized[normalized.length - 1].close;
}

/**
 * Extract the latest scalar from library indicator chart series.
 */
export function latestIndicatorValue(formattedSeries) {
  if (!formattedSeries) return null;

  if (Array.isArray(formattedSeries)) {
    if (formattedSeries.length === 0) return null;
    const last = formattedSeries[formattedSeries.length - 1];
    const val = typeof last === 'number' ? last : last?.value;
    return Number.isFinite(Number(val)) ? Number(val) : null;
  }

  if (typeof formattedSeries !== 'object') return null;
  const preferredKeys = ['RSI', 'SMA', 'EMA', 'WMA', 'DEMA', 'TEMA', 'CCI', 'ADX', 'MACD', 'SlowK', 'upper', 'adx', 'k'];
  for (const key of preferredKeys) {
    const series = formattedSeries[key];
    if (Array.isArray(series) && series.length > 0) {
      const last = series[series.length - 1];
      const val = typeof last === 'number' ? last : last?.value;
      if (Number.isFinite(Number(val))) return Number(val);
    }
  }
  const firstKey = Object.keys(formattedSeries)[0];
  if (!firstKey) return null;
  const series = formattedSeries[firstKey];
  if (!Array.isArray(series) || series.length === 0) return null;
  const last = series[series.length - 1];
  const val = typeof last === 'number' ? last : last?.value;
  return Number.isFinite(Number(val)) ? Number(val) : null;
}

export function computeIndicatorScalar(bars, indicatorId, params = {}) {
  const points = computeIndicatorSeriesPoints(bars, indicatorId, params);
  if (!points.length) return null;
  return points[points.length - 1].value;
}

/**
 * Full indicator series as [{ time, value }, ...] (oldest → newest).
 */
export function computeIndicatorSeriesPoints(bars, indicatorId, params = {}) {
  if (!bars?.length || !indicatorId) return [];
  try {
    const normalized = normalizeBarsForIndicator(bars);
    if (!normalized.length) return [];
    const values = calculateLibraryIndicator(normalized, indicatorId, params);
    if (!values) return [];
    const formatted = formatForChart(indicatorId, values);
    if (!Array.isArray(formatted)) return [];
    return formatted
      .map((pt) => {
        const value = typeof pt === 'number' ? pt : pt?.value;
        const time = typeof pt === 'object' ? pt?.time : null;
        return Number.isFinite(Number(value))
          ? { time, value: Number(value) }
          : null;
      })
      .filter(Boolean);
  } catch (_) {
    return [];
  }
}

/**
 * Evaluate custom checklist items.
 */
export function evaluateChecklistItems(items, { bars, manualValues = {} } = {}) {
  const list = Array.isArray(items) ? items : [];
  const normalizedBars = normalizeBarsForIndicator(bars);
  const results = list.map((raw, index) => {
    const item = normalizeChecklistItem(raw, index);
    if (item.type === 'manual') {
      const checked = !!manualValues[item.key];
      return {
        key: item.key,
        label: item.label || 'Checklist item',
        type: 'manual',
        passed: checked,
        checked,
        auto: false,
      };
    }

    const ind = item.indicator || {};
    const overlay = isOverlayIndicator(ind.id);
    const series = computeIndicatorSeriesPoints(normalizedBars, ind.id, ind.params);
    const indicatorValue = series.length ? series[series.length - 1].value : null;
    const prevIndicatorValue = series.length > 1 ? series[series.length - 2].value : null;
    const close = normalizedBars.length
      ? normalizedBars[normalizedBars.length - 1].close
      : null;
    const prevClose = normalizedBars.length > 1
      ? normalizedBars[normalizedBars.length - 2].close
      : null;

    let passed = false;
    let actual = indicatorValue;
    let prevActual = prevIndicatorValue;
    let expected = Number(ind.value);
    let compareLeft = 'indicator';

    if (overlay) {
      // Price vs moving average — crossings use previous price vs previous MA
      compareLeft = 'price';
      actual = close;
      prevActual = prevClose;
      expected = indicatorValue;
      passed = evaluateConditionOp(ind.op, {
        actual: close,
        expected: indicatorValue,
        prevActual: prevClose,
        prevExpected: prevIndicatorValue,
      });
    } else {
      // Oscillator vs threshold — crossings use previous indicator vs same threshold
      passed = evaluateConditionOp(ind.op, {
        actual: indicatorValue,
        expected: Number(ind.value),
        prevActual: prevIndicatorValue,
        prevExpected: Number(ind.value),
      });
    }

    return {
      key: item.key,
      label: item.label || formatIndicatorCondition(ind),
      type: 'indicator',
      passed: !!passed,
      auto: true,
      actual,
      prevActual,
      expected: Number.isFinite(expected) ? expected : null,
      indicatorValue,
      prevIndicatorValue,
      close,
      prevClose,
      compareLeft,
      op: ind.op,
      indicatorId: ind.id,
      period: ind.params?.time_period ?? ind.params?.period ?? null,
      condition: formatIndicatorCondition(ind),
    };
  });

  const passedCount = results.filter((r) => r.passed).length;
  return {
    results,
    allPassed: results.length === 0 || passedCount === results.length,
    passedCount,
    total: results.length,
  };
}

function parseTimeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const [h, m] = timeStr.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/**
 * Check whether `now` falls in any trading window for the given IANA timezone.
 */
export function isWithinTradingHours(windows, timezone = 'America/New_York', now = new Date()) {
  if (!Array.isArray(windows) || windows.length === 0) return true;
  let currentMinutes;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(now);
    const hour = Number(parts.find((p) => p.type === 'hour')?.value);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value);
    const h = hour === 24 ? 0 : hour;
    currentMinutes = h * 60 + minute;
  } catch (_) {
    currentMinutes = now.getHours() * 60 + now.getMinutes();
  }

  for (const window of windows) {
    const startField = window.startTime || window.start_time;
    const endField = window.endTime || window.end_time;
    const startMinutes = parseTimeToMinutes(startField);
    const endMinutes = parseTimeToMinutes(endField);
    if (startMinutes == null || endMinutes == null) continue;
    if (currentMinutes >= startMinutes && currentMinutes < endMinutes) return true;
  }
  return false;
}

function isRuleActive(enforcementConfig, ruleKey) {
  const cfg = enforcementConfig?.[ruleKey];
  if (!cfg) return false;
  return !!(cfg.enabled || cfg.enforce || cfg.alert);
}

function getRuleEnforcementMode(enforcementConfig, ruleKey) {
  const cfg = enforcementConfig?.[ruleKey] || {};
  if (cfg.enforce) return 'enforce';
  if (cfg.alert) return 'alert';
  if (cfg.enabled) return 'track';
  return 'off';
}

/**
 * Evaluate trading-rules rows for the live ticket (display only; server still enforces).
 */
export function evaluateTradeRulesForTicket({
  settings,
  orderForm,
  now = new Date(),
  checkHours = isWithinTradingHours,
} = {}) {
  const enforcement = settings?.enforcement_config || {};
  const thresholds = settings?.rule_thresholds || {};
  const rows = [];

  if (isRuleActive(enforcement, 'trading_window')) {
    const windows = thresholds.trading_windows || [];
    const tz = thresholds.trading_window_timezone || 'America/New_York';
    const within = typeof checkHours === 'function'
      ? !!checkHours(windows, tz, now)
      : true;
    rows.push({
      key: 'trading_window',
      label: 'Trading hours',
      passed: within,
      auto: true,
      detail: within ? 'Within allowed window' : 'Outside trading window',
      enforcementMode: getRuleEnforcementMode(enforcement, 'trading_window'),
    });
  }

  if (isRuleActive(enforcement, 'require_stop_loss')) {
    const stopEnabled = !!(orderForm?.brackets?.stopLoss?.enabled);
    const stopPrice = Number(orderForm?.brackets?.stopLoss?.stopPrice);
    const hasStop = stopEnabled && Number.isFinite(stopPrice) && stopPrice > 0;
    rows.push({
      key: 'require_stop_loss',
      label: 'Stop loss set',
      passed: hasStop,
      auto: true,
      detail: hasStop ? `Stop @ ${stopPrice}` : 'No stop loss on ticket',
      enforcementMode: getRuleEnforcementMode(enforcement, 'require_stop_loss'),
    });
  }

  if (isRuleActive(enforcement, 'max_risk_per_trade')) {
    // Risk is only knowable with an attached stop; a lone entry order cannot define max loss.
    // Avoid Number(null) === 0, which previously showed "$0.00 / max" as a false pass.
    const maxRisk = Number(thresholds.max_risk_per_trade);
    const stopEnabled = !!(orderForm?.brackets?.stopLoss?.enabled);
    const stopPrice = Number(orderForm?.brackets?.stopLoss?.stopPrice);
    const hasStop = stopEnabled && Number.isFinite(stopPrice) && stopPrice > 0;
    const riskRaw = orderForm?.computedRisk;
    const risk = riskRaw == null || riskRaw === '' ? NaN : Number(riskRaw);
    const hasMax = Number.isFinite(maxRisk) && maxRisk > 0;
    const hasRisk = hasStop && Number.isFinite(risk) && risk >= 0;
    const within = !hasMax || (hasRisk && risk <= maxRisk);
    let detail = 'Max risk not configured';
    if (hasMax) {
      if (!hasRisk) {
        detail = 'Set a stop loss to calculate risk';
      } else {
        detail = `Risk $${risk.toFixed(2)} / max $${maxRisk.toFixed(2)}`;
      }
    }
    rows.push({
      key: 'max_risk_per_trade',
      label: 'Within max risk',
      passed: within,
      auto: true,
      detail,
      enforcementMode: getRuleEnforcementMode(enforcement, 'max_risk_per_trade'),
    });
  }

  const passedCount = rows.filter((r) => r.passed).length;
  return {
    results: rows,
    allPassed: rows.length === 0 || passedCount === rows.length,
    passedCount,
    total: rows.length,
  };
}

export function buildChecklistEntryPayload({
  template,
  checklistEval,
  rulesEval,
  orderForm,
  manualValues,
} = {}) {
  return {
    template: normalizeChecklistTemplate(template || DEFAULT_CHECKLIST_TEMPLATE),
    checklist: checklistEval?.results || [],
    rules: rulesEval?.results || [],
    manualValues: manualValues || {},
    ticket: {
      quantity: orderForm?.quantity ?? null,
      orderType: orderForm?.orderType ?? null,
      symbol: orderForm?.symbol ?? null,
      side: orderForm?.side ?? null,
      stopPrice: orderForm?.brackets?.stopLoss?.stopPrice ?? null,
      stopEnabled: !!orderForm?.brackets?.stopLoss?.enabled,
      computedRisk: orderForm?.computedRisk ?? null,
    },
    evaluatedAt: new Date().toISOString(),
  };
}

export function resolveChecklistAttemptStatus({
  blockUntilComplete,
  checklistComplete,
  enforcementBlocked,
} = {}) {
  if (blockUntilComplete && !checklistComplete) return 'blocked_checklist';
  if (enforcementBlocked) return 'rejected';
  return 'submitted';
}
